import { Box, Stack, Typography } from '@mui/material';
import { alpha } from '@mui/material/styles';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import SmartToyOutlinedIcon from '@mui/icons-material/SmartToyOutlined';
import { GlassPanel } from '../../../shared/components/GlassPanel';
import { IconTile } from '../../../shared/components/IconTile';
import { SoftButton } from '../../../shared/components/SoftButton';
import type { DocumentType, DocumentTypeSection } from '../../../core/types/data-models';

/**
 * A not-yet-generated document type, shown alongside a role's existing
 * document cards in the row layout — the same recommendation surfaced in
 * Next Steps, scoped to this role's category, with a one-click way to start
 * it. `sections` are the doc type's top-level (root) sections, given as a
 * preview of what generating it will produce.
 */
export function RecommendedDocumentCard({
  docType,
  sections,
  canGenerate,
  onGenerate,
  roleColor,
}: {
  docType: DocumentType;
  sections: DocumentTypeSection[];
  canGenerate: boolean;
  onGenerate: () => void;
  /** Tints the icon tile and "Suggested" badge with the owning assistant's own color. */
  roleColor: string;
}) {
  return (
    <GlassPanel hover sx={{ minWidth: 0, display: 'flex', flexDirection: 'column', gap: 0.75, p: 1.5 }}>
      <Stack direction="row" spacing={1} alignItems="center" justifyContent="space-between">
        <Stack direction="row" spacing={1} alignItems="center" sx={{ minWidth: 0 }}>
          <IconTile
            icon={<SmartToyOutlinedIcon fontSize="inherit" />}
            size="sm"
            sx={{ color: roleColor, backgroundColor: alpha(roleColor, 0.18), borderColor: alpha(roleColor, 0.4) }}
          />
          <Typography variant="body2" fontWeight={700} noWrap>
            {docType.name}
          </Typography>
        </Stack>
        <Box
          component="span"
          sx={{
            display: 'inline-flex',
            alignItems: 'center',
            px: 1.1,
            py: 0.3,
            borderRadius: 'var(--radius-full)',
            fontSize: 11,
            fontWeight: 700,
            lineHeight: 1.6,
            fontFamily: 'var(--font-mono)',
            color: roleColor,
            backgroundColor: alpha(roleColor, 0.14),
            border: '1px solid',
            borderColor: alpha(roleColor, 0.4),
            flexShrink: 0,
          }}
        >
          Suggested
        </Box>
      </Stack>
      <Typography
        variant="caption"
        color="text.secondary"
        sx={{
          display: '-webkit-box',
          WebkitLineClamp: 2,
          WebkitBoxOrient: 'vertical',
          overflow: 'hidden',
          lineHeight: 1.4,
          minHeight: '2.8em',
        }}
      >
        {docType.description || 'No description'}
      </Typography>

      {sections.length > 0 && (
        <Stack spacing={0.4}>
          {sections.map((s) => (
            <Stack key={s.id} direction="row" spacing={0.6} alignItems="center">
              <Box sx={{ width: 4, height: 4, borderRadius: '50%', backgroundColor: 'text.disabled', flexShrink: 0 }} />
              <Typography variant="caption" color="text.secondary" noWrap sx={{ minWidth: 0 }}>
                {s.title}
              </Typography>
            </Stack>
          ))}
        </Stack>
      )}

      {canGenerate && (
        <SoftButton
          tone="success"
          size="small"
          endIcon={<ArrowForwardIcon sx={{ fontSize: '13px !important' }} />}
          onClick={onGenerate}
          sx={{ alignSelf: 'flex-start', mt: 'auto', fontSize: 11, fontWeight: 700 }}
        >
          Generate
        </SoftButton>
      )}
    </GlassPanel>
  );
}

/**
 * A not-yet-generated document type, shown in the column layout's "Linked
 * Artifacts" list alongside real documents — same recommendation as
 * {@link RecommendedDocumentCard}, but trimmed to a single row (no
 * description or section preview) to match that list's compact presentation.
 */
export function RecommendedArtifactRow({
  docType,
  canGenerate,
  onGenerate,
}: {
  docType: DocumentType;
  canGenerate: boolean;
  onGenerate: () => void;
}) {
  return (
    <Stack
      direction="row"
      spacing={0.75}
      alignItems="center"
      sx={{
        px: 1,
        py: 0.5,
        borderRadius: 'var(--radius-sm)',
        border: '1px dashed',
        borderColor: 'divider',
        bgcolor: 'transparent',
      }}
    >
      <Typography
        variant="caption"
        color="text.disabled"
        sx={{ flex: 1, minWidth: 0, fontStyle: 'italic' }}
        noWrap
      >
        {docType.name}
      </Typography>
      {canGenerate && (
        <SoftButton
          tone="success"
          size="small"
          onClick={onGenerate}
          sx={{ flexShrink: 0, minHeight: 0, py: 0.25, px: 1, fontSize: 9, fontWeight: 700 }}
        >
          Generate
        </SoftButton>
      )}
    </Stack>
  );
}
